<template>
  <div>
    <!-- client only to prevent hydration mismatch because of headless ui -->
    <!-- remove this client only to test -->
    <ClientOnly>
      <UAccordion :items="filterCategories" multiple>
        <template #default="{ item, open }">
          <div
            :id="`wrap-${item.filterId}`"
            :data-id="`wrap-${item.filterId}`"
            class="flex items-center justify-between pt-6 pb-2.5"
            role="button"
            :aria-label="`${item.label} filter`"
          >
            <FrontendServiceFilterAccordionButton
              :open="open"
              :label="item.label"
              :icon="item.icon"
              :toggleable="toggleable"
              :filter-id="item.filterId"
              :hide-accordian-header="item.hideAccordianHeader"
            >
              <FrontendServiceFilterClearBadge v-if="item.badge" :badge="item.badge" />
            </FrontendServiceFilterAccordionButton>
          </div>
        </template>
        <template #item="{ item }">
          <!-- Check if component is Support Need filter as that requires props and an emit -->
          <div v-if="item.key === 'supportNeeds' && supportNeedCategories.length" class="pb-3">
            <FrontendServiceFilterSupportNeeds
              :support-need-categories="supportNeedCategories"
              :toggleable="toggleable"
              @open-support-need-glossary="isOpen = true"
            />
            <ModalSupportNeedGlossary v-model="isOpen" />
          </div>

          <!-- Get rest of regular filters -->
          <div v-else class="pb-3">
            <component :is="item.component" :toggleable="toggleable" />
          </div>
        </template>
      </UAccordion>
    </ClientOnly>
  </div>
</template>
<script setup lang="ts">
// Import filter components
import type { Component } from 'vue'
import SupportNeeds from '~/components/Frontend/Service/Filter/SupportNeeds.vue'
import OpeningTimes from '~/components/Frontend/Service/Filter/OpeningTimes.vue'
import Preferences from '~/components/Frontend/Service/Filter/Preferences.vue'
import AgeBracket from '~/components/Frontend/Service/Filter/AgeBracket.vue'
import DeliveryFormats from '~/components/Frontend/Service/Filter/DeliveryFormats.vue'
import ServiceProviderType from '~/components/Frontend/Service/Filter/ServiceProviderTypes.vue'
//import Amenities from '~/components/Frontend/Service/Filter/AmenitiesAndAccessibility.vue'
import National from '~/components/Frontend/Service/Filter/NationalSupport.vue'
import type { FilterIds } from '~/types/FilterIds'

const props = defineProps({
  toggleable: Boolean
})

// Get filters from service search store
const serviceSearchStore = useServiceSearchStore()
const { filters } = storeToRefs(serviceSearchStore)

// Get support need store for front-end
const frontendSupportNeedCategorySearchStore = useSupportNeedCategoryStore()
const { supportNeedCategories } = storeToRefs(frontendSupportNeedCategorySearchStore)

// get the disabled filters from the search store
const { disabledFilters } = storeToRefs(useServiceSearchStore())

const { ageBrackets, selectedAgeRange } = useAgeBrackets(true)

const { isNative } = useRuntimeConfig().public

await useAsyncData('snc', () => frontendSupportNeedCategorySearchStore.fetchSupportNeedCategories(), {
  server: !isNative,
  lazy: true,
  deep: false,
  dedupe: 'defer'
})

// Control state of support-need modal
const supportNeedGlossaryStore = useSupportNeedGlossaryStore()
const { isOpen } = storeToRefs(supportNeedGlossaryStore)

interface FilterCat {
  key: string
  label: string
  defaultOpen: boolean
  component: Component
  icon: string
  filterId: FilterIds
  hideAccordianHeader?: boolean
  badge?: number | string
  clear?: () => void
}

const filterCategories = computed(() => {
  const [min = 0, max = 122] = filters.value.ageRange || []
  // we also need to account for if the age range is 0-122 or empty
  // if the age range is 0-122 or empty, we need to show 122+

  let ageBadge = ''
  if (!filters.value.ageRange?.length || (min === 0 && max === 122)) {
    ageBadge = 'All'
  } else {
    ageBadge = ''
    // if the max is 11, we need to show '< 12'
    const calculatedMax = max === 11 ? '12' : max
    // if the min is 19, we need to show '18+'
    const calculatedMin = min === 19 ? '18' : min

    ageBadge =
      max > 25 ? `${calculatedMin}+` : min === 0 ? '< ' + calculatedMax : `${calculatedMin}-` + calculatedMax
  }

  const isMobileDevice = /Mobi|Android/i.test(navigator?.userAgent || '')

  const items: FilterCat[] = [
    {
      key: 'supportNeeds',
      label: 'Support need',
      defaultOpen: true,
      component: SupportNeeds,
      icon: 'mdi:hand-heart-outline',
      filterId: 'support-needs',
      badge: filters.value.supportNeeds.length,
      clear: () => {
        filters.value.supportNeeds = []
      }
    },
    {
      key: 'openingTimes',
      label: 'Opening times',
      defaultOpen: true,
      component: OpeningTimes,
      icon: 'mdi:clock-outline',
      filterId: 'opening-times',
      badge: [
        filters.value.open247,
        filters.value.openEvenings,
        filters.value.openWeekends,
        filters.value.openNow
      ].filter(Boolean).length,
      clear: () => {
        filters.value.open247 = false
        filters.value.openEvenings = false
        filters.value.openWeekends = false
        filters.value.openNow = false
      }
    },
    {
      key: 'preferences',
      label: 'Preferences',
      defaultOpen: true,
      component: Preferences,
      icon: 'mdi:account-outline',
      filterId: 'preferences',
      badge: filters.value.preferences.length,
      clear: () => {
        filters.value.preferences = []
      }
    },
    {
      key: 'ageBracket',
      label: 'Age bracket',
      defaultOpen: !isMobileDevice,
      component: AgeBracket,
      icon: 'mdi:face-man-outline',
      filterId: 'age-bracket',
      badge: ageBadge,
      clear: () => {
        filters.value.ageRange = []
      }
    },
    {
      key: 'deliveryFormat',
      label: 'Type of service',
      defaultOpen: !isMobileDevice,
      component: DeliveryFormats,
      icon: 'mdi:home-account',
      filterId: 'type-of-service',
      badge: filters.value.deliveryFormats.length,
      clear: () => {
        filters.value.deliveryFormats = []
      }
    },
    // NATIONAL
    {
      key: 'national',
      label: 'National support only',
      defaultOpen: !isMobileDevice,
      component: National,
      icon: 'mdi:certificate-outline',
      filterId: 'national-support-only',
      hideAccordianHeader: true
    },
    // END NATIONAL
    {
      key: 'serviceProvider',
      label: 'Service provider',
      defaultOpen: !isMobileDevice,
      component: ServiceProviderType,
      icon: 'mdi:office-building-plus',
      filterId: 'service-provider',
      badge: filters.value.serviceProviderTypes.length,
      clear: () => {
        filters.value.serviceProviderTypes = []
      }
    }
    /**
    {
      key: 'amenities',
      label: 'Amenities',
      defaultOpen: !isMobileDevice,
      component: Amenities,
      icon: 'mdi:home-plus-outline',
      filterId: 'amenities',
      badge: filters.value.amenitiesAndAccessibility.length || 0,
      clear: () => {
        filters.value.amenitiesAndAccessibility = []
      }
    }
    */
  ]

  // if we're in toggleable mode, always return all items
  if (props.toggleable) return items
  // otherwise, filter them out by disabled items
  const disabled = disabledFilters.value || []
  return items.filter((x) => !disabled.includes(x.filterId))
})
</script>
